import { RendererContextType } from '../../../../contexts/RendererContext';
import { GroupType, ProjectByIdQuery } from '../../../../types/graphqlTypes';
import { PointoramaFloorMesh3D, FloorMesh3DChildren } from '../../../../potree/utils/PointoramaFloorMesh3D';
import { SyncConfig } from './syncFromGraphQL';
import { resolveFloorGroupIdentifier } from '../../../../utils/roomFolders';

type Project = ProjectByIdQuery['projectById'];
type Group = Project['state']['groups'][number];
type Annotation = Project['state']['annotations'][number];

/**
 * Build the FloorMesh3DChildren for one floor group: the outline annotation
 * sitting under it, every ROOM under it, and EVERY opening in the project.
 *
 * Opening → room linkage is resolved lazily inside `PointoramaFloorMesh3D`
 * because `opening_room_ids` is a reactive property that may be populated
 * *after* the OPENING annotation appears in the GraphQL list. Passing every
 * opening id in and subscribing to its room-ids prop lets the mesh rebuild
 * whenever an opening's linkage changes (including from "no rooms" to
 * "linked to a room on my floor") without relying on annotation-id churn
 * to retrigger this sync hook.
 */
function resolveChildren(floorId: string, annotations: Annotation[], groups: Group[]): FloorMesh3DChildren {
  let outlineId: string | null = null;
  let wallSketchId: string | null = null;
  const roomIds: string[] = [];

  for (const a of annotations) {
    if (a.groupIdentifier !== floorId) continue;
    if (a.type === 'OUTLINE') outlineId = a.identifier;
    else if (a.type === 'WALL_SKETCH') wallSketchId = a.identifier;
    else if (a.type === 'ROOM') roomIds.push(a.identifier);
  }

  const openingIds: string[] = [];
  for (const a of annotations) {
    if (a.type === 'OPENING') openingIds.push(a.identifier);
  }
  // The floor's opening sketches: under the floor itself, or under one of its
  // room folders (their scene objects resolve the floor the same way).
  const openingSketchIds: string[] = [];
  for (const a of annotations) {
    if (a.type === 'OPENING_SKETCH' && resolveFloorGroupIdentifier(groups, a.groupIdentifier) === floorId) openingSketchIds.push(a.identifier);
  }

  return { outlineId, wallSketchId, roomIds, openingIds, openingSketchIds };
}

function childrenEqual(a: FloorMesh3DChildren, b: FloorMesh3DChildren): boolean {
  if (a.outlineId !== b.outlineId) return false;
  if (a.wallSketchId !== b.wallSketchId) return false;
  if (a.roomIds.length !== b.roomIds.length) return false;
  if (a.openingIds.length !== b.openingIds.length) return false;
  // Order matches because both are derived from the same annotations array.
  for (let i = 0; i < a.roomIds.length; i++) if (a.roomIds[i] !== b.roomIds[i]) return false;
  for (let i = 0; i < a.openingIds.length; i++) if (a.openingIds[i] !== b.openingIds[i]) return false;
  if (a.openingSketchIds.length !== b.openingSketchIds.length) return false;
  for (let i = 0; i < a.openingSketchIds.length; i++) if (a.openingSketchIds[i] !== b.openingSketchIds[i]) return false;
  return true;
}

/**
 * Cache of last-seen children per floor — lets us skip `setChildren` (which
 * unsubscribes + resubscribes every geometry listener) when nothing changed.
 */
const lastChildrenByFloorId = new Map<string, FloorMesh3DChildren>();

export function createSyncFloorMeshes3DConfig({
  viewer,
  groups,
  annotations,
}: {
  viewer: NonNullable<RendererContextType['viewer']>;
  groups: Project['state']['groups'];
  annotations: Annotation[];
}): SyncConfig<Group, PointoramaFloorMesh3D> {
  return {
    getSourceItems: () => groups.filter((g) => g.type === GroupType.Floor),
    getSceneItems: () => [...(viewer.scene.floorMeshes3D || [])],
    getSourceIdentifier: (item) => item.identifier,
    getSceneIdentifier: (item) => item.identifier,
    update: (source, scene) => {
      const next = resolveChildren(source.identifier, annotations, groups);
      const prev = lastChildrenByFloorId.get(source.identifier);
      if (prev && childrenEqual(prev, next)) return 'unchanged';
      lastChildrenByFloorId.set(source.identifier, next);
      scene.setChildren(next);
      return 'updated';
    },
    create: (source) => {
      const mesh = new PointoramaFloorMesh3D(viewer);
      mesh.identifier = source.identifier;
      const children = resolveChildren(source.identifier, annotations, groups);
      lastChildrenByFloorId.set(source.identifier, children);
      mesh.setChildren(children);
      return mesh;
    },
    addToScene: (item) => viewer.scene.addFloorMesh3D(item),
    removeFromScene: (item) => {
      lastChildrenByFloorId.delete(item.identifier);
      viewer.scene.removeFloorMesh3D(item);
    },
  };
}
